import React, { useState, useEffect, useRef } from 'react';
import { usePhotoCapture } from './usePhotoCapture';
import { setUnsavedCapture } from '../shared/unsavedWork';
import { PhotoViewfinder } from './PhotoViewfinder';
import { PhotoReview } from './PhotoReview';
import { PhotoLightbox } from './PhotoLightbox';
import { PhotoNotes } from './PhotoNotes';
import { PhotoAnnotationLayer } from './PhotoAnnotationLayer';
import type { InitialDraft } from '../shared';
import type { PhotoAnnotation, PhotoConfig } from '../../../../types/photo.types';
import './PhotoCapture.css';

export type PhotoStep = 'viewfinder' | 'review' | 'lightbox' | 'annotate' | 'notes';

export interface PhotoCaptureProps {
  taskName: string;
  prompt: string;
  onSubmit: (photoBlobs: Blob[], notes: string, photoAnnotations?: PhotoAnnotation[]) => void;
  onSaveDraft: (photoBlobs: Blob[], notes: string) => void;
  onClose: () => void;
  initialNotes?: string;
  initialDraft?: InitialDraft;
  photoConfig?: PhotoConfig;
}

export const PhotoCapture: React.FC<PhotoCaptureProps> = ({
  taskName,
  onSubmit,
  onSaveDraft,
  onClose,
  initialNotes,
  initialDraft,
  photoConfig,
}) => {
  const hasDraftMedia = !!initialDraft?.mediaBlobs.length;
  const [step, setStep] = useState<PhotoStep>(hasDraftMedia ? 'review' : 'viewfinder');
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const capture = usePhotoCapture();
  const [annotations, setAnnotations] = useState<Omit<PhotoAnnotation, 'caption'>[]>([]);
  const annotationsEnabled = !!photoConfig?.annotations?.enabled;
  const injectedRef = useRef(false);

  /* Captured photos not yet saved → page-level leave nudge */
  useEffect(() => {
    setUnsavedCapture(capture.photos.length > 0);
    return () => setUnsavedCapture(false);
  }, [capture.photos.length]);

  /* Inject draft photos on mount */
  useEffect(() => {
    if (hasDraftMedia && !injectedRef.current) {
      injectedRef.current = true;
      capture.injectPhotos(initialDraft!.mediaBlobs);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Capture a photo — stay in viewfinder for continuous shooting */
  const handleCapture = () => {
    capture.takePhoto();
  };

  const handleGalleryFiles = (files: FileList) => {
    capture.addPhotosFromFiles(files);
  };

  const handleDeleteInReview = (index: number) => {
    capture.deletePhoto(index);
    if (capture.photos.length <= 1) {
      setStep('viewfinder');
    }
  };

  const handlePhotoTap = (index: number) => {
    setLightboxIndex(index);
    setStep('lightbox');
  };

  const handleSubmit = (notes: string) => {
    capture.stopCamera();
    const photoAnnotations: PhotoAnnotation[] = annotations.map(a => ({
      ...a,
      caption: { text: notes },
    }));
    onSubmit(capture.photoBlobs, notes, photoAnnotations.length > 0 ? photoAnnotations : undefined);
  };

  const handleSaveDraft = (notes: string) => {
    capture.stopCamera();
    onSaveDraft(capture.photoBlobs, notes);
  };

  const handleClose = () => {
    capture.stopCamera();
    onClose();
  };

  return (
    <>
      {step === 'viewfinder' && (
        <PhotoViewfinder
          taskName={taskName}
          videoRef={capture.videoRef}
          isReady={capture.isReady}
          error={capture.error}
          facingMode={capture.facingMode}
          lastPhoto={capture.photos.length > 0 ? capture.photos[capture.photos.length - 1] : null}
          photoCount={capture.photos.length}
          zoomLevel={capture.zoomLevel}
          maxZoom={capture.maxZoom}
          exposure={capture.exposure}
          exposureRange={capture.exposureRange}
          onCapture={handleCapture}
          onFlip={capture.flipCamera}
          onStartCamera={() => capture.startCamera()}
          onReview={() => setStep('review')}
          onClose={handleClose}
          onZoom={capture.applyZoom}
          onExposure={capture.applyExposure}
          onGalleryFiles={handleGalleryFiles}
        />
      )}

      {step === 'review' && (
        <PhotoReview
          taskName={taskName}
          photos={capture.photos}
          onDeletePhoto={handleDeleteInReview}
          onPhotoTap={handlePhotoTap}
          onAddMore={() => setStep('viewfinder')}
          onContinue={() => setStep(annotationsEnabled ? 'annotate' : 'notes')}
          onClose={handleClose}
        />
      )}

      {step === 'lightbox' && (
        <PhotoLightbox
          photos={capture.photos}
          initialIndex={lightboxIndex}
          onClose={() => setStep('review')}
        />
      )}

      {step === 'annotate' && capture.photos.length > 0 && (
        <PhotoAnnotationLayer
          taskName={taskName}
          photoUrl={capture.photos[0]}
          canDraw={!!photoConfig?.annotations?.drawing}
          canPin={!!photoConfig?.annotations?.pins}
          canVoicePin={!!(photoConfig?.annotations?.pins && photoConfig?.annotations?.voicePins)}
          annotationPrompt={photoConfig?.annotationPrompt}
          onComplete={(annotation) => {
            setAnnotations([annotation]);
            setStep('notes');
          }}
          onSkip={() => setStep('notes')}
          onClose={handleClose}
        />
      )}

      {step === 'notes' && (
        <PhotoNotes
          taskName={taskName}
          photos={capture.photos}
          onAddMore={() => setStep('viewfinder')}
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
          onClose={handleClose}
          initialNotes={initialDraft?.notes || initialNotes}
        />
      )}
    </>
  );
};
